import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header2 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template2' },
        { label: 'Shop', path: '/template2/shop' },
        { label: 'About', path: '/template2/about' },
        { label: 'Contact', path: '/template2/contact' },
        { label: 'Orders', path: '/template2/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#0A0A0A' : 'transparent',
                    borderBottom: scrolled ? '1px solid #2A2A2A' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '70px',
                    }}
                    className="md:px-6"
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template2')}
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '22px',
                            fontWeight: 800,
                            color: '#FFFFFF',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                        }}
                        className="md:text-[26px]"
                    >
                        RANG
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#A3A3A3',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    letterSpacing: '1.5px',
                                    textTransform: 'uppercase',
                                    padding: '8px 0',
                                    borderBottom: '2px solid transparent',
                                    transition: 'color 0.3s ease, border-color 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#FFFFFF';
                                    e.currentTarget.style.borderBottomColor = '#FF6B35';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#A3A3A3';
                                    e.currentTarget.style.borderBottomColor = 'transparent';
                                }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Search
                            className="w-5 h-5 cursor-pointer hidden md:block"
                            style={{ color: '#A3A3A3', transition: 'color 0.3s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B35'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#A3A3A3'; }}
                        />
                        <div
                            className="relative cursor-pointer"
                            onClick={() => navigate('/template2/cart')}
                        >
                            <ShoppingCart
                                className="w-5 h-5"
                                style={{ color: '#FFFFFF', transition: 'color 0.3s' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B35'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#FFFFFF'; }}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#FF6B35',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: "'Montserrat', sans-serif",
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>
                        <User
                            className="w-5 h-5 cursor-pointer hidden md:block"
                            style={{ color: '#A3A3A3', transition: 'color 0.3s' }}
                            onClick={() => navigate('/template2/profile')}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B35'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#A3A3A3'; }}
                        />

                        {/* Mobile Menu Toggle */}
                        <button
                            className="md:hidden"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            {mobileMenuOpen ? (
                                <X className="w-6 h-6" style={{ color: '#FFFFFF' }} />
                            ) : (
                                <Menu className="w-6 h-6" style={{ color: '#FFFFFF' }} />
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 49,
                        backgroundColor: '#0A0A0A',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '24px',
                    }}
                >
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#FFFFFF',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                padding: '12px 0',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B35'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#FFFFFF'; }}
                        >
                            {link.label}
                        </button>
                    ))}
                    <div style={{ width: '40px', height: '2px', backgroundColor: '#FF6B35', margin: '8px 0' }} />
                    <button
                        onClick={() => handleNavClick('/template2/profile')}
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#A3A3A3',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                        }}
                    >
                        My Account
                    </button>
                </div>
            )}
        </>
    );
};

export default Header2;
